import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { AcoesDaLinha, BotaoVer, BotaoEditar, BotaoExcluir } from '../../components/BotoesAcao';
import { useConfirm } from '../../components/ConfirmDialog';
import { Manutencao } from '../../types';
import { manutencoesService, ManutencoesResponse, ManutencoesFilters } from '../../services/manutencoes';
import ExportButtons from '../../components/ExportButtons';
import { exportService } from '../../services/export';
import { parseApiDate } from '../../utils/formatters';
import { usePermissoes } from '../../hooks/usePermissoes';
import AvisoSemPermissao from '../../components/AvisoSemPermissao';
import { ehSemPermissao, mensagemDoErro } from '../../utils/erros';

const ManutencoesList: React.FC = () => {
  const { pode } = usePermissoes();
  const [filters, setFilters] = useState<ManutencoesFilters>({
    veiculo_id: '',
    tipo: '',
    status: '',
    data_inicio: '',
    data_fim: '',
    valor_min: '',
    valor_max: '',
    sort_by: 'data_manutencao',
    sort_order: 'desc',
    per_page: 15,
    page: 1
  });

  const navigate = useNavigate();
  const confirm = useConfirm();

  const { data, isLoading, error, refetch } = useQuery<ManutencoesResponse>({
    queryKey: ['manutencoes', filters],
    queryFn: () => manutencoesService.getManutencoes(filters),
    placeholderData: (previousData) => previousData
  });

  const handleDelete = async (manutencao: Manutencao) => {
    const ok = await confirm({
      title: 'Excluir manutenção',
      message: `Tem certeza que deseja excluir esta manutenção${manutencao.veiculo ? ` do veículo ${manutencao.veiculo.placa}` : ''}? Esta ação não pode ser desfeita.`,
      confirmLabel: 'Excluir',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await manutencoesService.deleteManutencao(manutencao.id);
      toast.success('Manutenção excluída com sucesso');
      refetch();
    } catch (err) {
      toast.error(mensagemDoErro(err, 'Erro ao excluir manutenção'));
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    // Voltar para a primeira pagina ao mudar um filtro esta certo: o resultado
    // e outro e a pagina 5 do resultado anterior nao significa nada. Mas isso
    // nao pode valer quando a chave alterada E a pagina, senao o botao Proxima
    // pede a pagina 2 e a linha seguinte devolve para a 1 — a paginacao fica
    // inutilizada sem nenhum sinal de erro.
    setFilters(prev => ({ ...prev, [key]: value, ...(key === 'page' ? {} : { page: 1 }) }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const getTipoManutencaoColor = (tipo: string) => {
    switch (tipo) {
      case 'preventiva': return 'bg-green-100 text-green-800';
      case 'corretiva': return 'bg-red-100 text-red-800';
      case 'emergencial': return 'bg-orange-100 text-orange-800';
      case 'revisao': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'agendada': return 'bg-yellow-100 text-yellow-800';
      case 'em_andamento': return 'bg-blue-100 text-blue-800';
      case 'concluida': return 'bg-green-100 text-green-800';
      case 'cancelada': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date: string) => {
    const d = parseApiDate(date);
    return d ? d.toLocaleDateString('pt-BR') : '';
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Tratar erros
  useEffect(() => {
    // Falta de permissão não vira aviso passageiro: a tela inteira explica o
    // que aconteceu, logo abaixo, e um toast só somaria ruído.
    if (error && !ehSemPermissao(error)) {
      toast.error(mensagemDoErro(error, 'Erro ao carregar manutenções'));
    }
  }, [error]);

  if (ehSemPermissao(error)) {
    return <AvisoSemPermissao erro={error} />;
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Manutenções</h1>
          <p className="text-gray-600">Controle de manutenções dos veículos</p>
        </div>
        <div className="flex items-center gap-3">
          {pode('relatorios', 'exportar') && (
            <ExportButtons
            onExportPdf={() => exportService.manutencoesPdf()}
            onExportExcel={() => exportService.manutencoesExcel()}
          />
          )}
          {pode('manutencoes', 'criar') && (
          <Link
            to="/manutencoes/novo"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nova Manutenção
          </Link>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Filtros</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Manutenção</label>
            <select
              value={filters.tipo}
              onChange={(e) => handleFilterChange('tipo', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="preventiva">Preventiva</option>
              <option value="corretiva">Corretiva</option>
              <option value="emergencial">Emergencial</option>
              <option value="revisao">Revisão</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="agendada">Agendada</option>
              <option value="em_andamento">Em Andamento</option>
              <option value="concluida">Concluída</option>
              <option value="cancelada">Cancelada</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Início</label>
            <input
              type="date"
              value={filters.data_inicio}
              onChange={(e) => handleFilterChange('data_inicio', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Fim</label>
            <input
              type="date"
              value={filters.data_fim}
              onChange={(e) => handleFilterChange('data_fim', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valor Mínimo</label>
            <input
              type="number"
              value={filters.valor_min}
              onChange={(e) => handleFilterChange('valor_min', e.target.value)}
              placeholder="0.00"
              step="0.01"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valor Máximo</label>
            <input
              type="number"
              value={filters.valor_max}
              onChange={(e) => handleFilterChange('valor_max', e.target.value)}
              placeholder="0.00"
              step="0.01"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Lista de Manutenções */}
      <div className="bg-white rounded-lg shadow-sm border">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando manutenções...</p>
          </div>
        ) : data?.data && data.data.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Veículo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Descrição
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quilometragem
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.data.map((manutencao: Manutencao) => (
                    <tr key={manutencao.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatDate(manutencao.data_manutencao)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {manutencao.oficina_nome}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {manutencao.veiculo?.placa}
                        </div>
                        <div className="text-sm text-gray-500">
                          {manutencao.veiculo?.marca} {manutencao.veiculo?.modelo}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTipoManutencaoColor(manutencao.tipo)}`}>
                          {manutencao.tipo}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {manutencao.descricao}
                        </div>
                        {manutencao.oficina_nome && (
                          <div className="text-sm text-gray-500">
                            {manutencao.oficina_nome}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatNumber(manutencao.quilometragem)} km
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(manutencao.valor_total)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(manutencao.status)}`}>
                          {manutencao.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <AcoesDaLinha>
                          <BotaoVer onClick={() => navigate(`/manutencoes/${manutencao.id}`)} title="Ver detalhes" />
                          {pode('manutencoes', 'editar') && <BotaoEditar onClick={() => navigate(`/manutencoes/${manutencao.id}/editar`)} />}
                          {pode('manutencoes', 'excluir') && <BotaoExcluir onClick={() => handleDelete(manutencao)} />}
                        </AcoesDaLinha>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {data.pagination && data.pagination.last_page > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(data.pagination.current_page - 1)}
                    disabled={data.pagination.current_page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => handlePageChange(data.pagination.current_page + 1)}
                    disabled={data.pagination.current_page === data.pagination.last_page}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Próximo
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando <span className="font-medium">{((data.pagination.current_page - 1) * data.pagination.per_page) + 1}</span> a{' '}
                      <span className="font-medium">
                        {Math.min(data.pagination.current_page * data.pagination.per_page, data.pagination.total)}
                      </span> de{' '}
                      <span className="font-medium">{data.pagination.total}</span> resultados
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(data.pagination.current_page - 1)}
                        disabled={data.pagination.current_page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => handlePageChange(data.pagination.current_page + 1)}
                        disabled={data.pagination.current_page === data.pagination.last_page}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Próximo
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma manutenção encontrada</h3>
            <p className="mt-1 text-sm text-gray-500">
              Comece registrando uma nova manutenção.
            </p>
            <div className="mt-6">
              <Link
                to="/manutencoes/novo"
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Nova Manutenção
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManutencoesList;
